<template>
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <div class="flex justify-between items-center border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
          Ajout multiple de services
        </h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          Créez plusieurs services en une seule fois pour gagner du temps.
        </p>
      </div>
      <button @click="router.push('/services')"
        class="px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm flex items-center gap-2"
        :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-300' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/></svg>
        Retour
      </button>
    </div>

    <div class="max-w-4xl space-y-4">
      <!-- Liste des services à créer -->
      <div v-for="(service, index) in services" :key="index"
        class="border rounded-2xl p-5 space-y-4 transition-all duration-200"
        :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">

        <div class="flex items-center justify-between mb-3">
          <div class="text-xs font-semibold uppercase tracking-wider transition-colors"
            :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
            Service #{{ index + 1 }}
          </div>
          <button v-if="services.length > 1" @click="removeService(index)"
            class="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            :class="isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'">
            Supprimer
          </button>
        </div>

        <div class="grid lg:grid-cols-2 gap-4">
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Nom du service *</label>
            <input v-model="service.nom" type="text" placeholder="Ex: Coupe & Styling"
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Catégorie *</label>
            <select v-model="service.categorie"
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none appearance-none cursor-pointer transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'">
              <option value="">Sélectionner une catégorie</option>
              <option v-for="cat in categories" :key="cat._id" :value="cat._id">{{ cat.nom }}</option>
            </select>
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Prix (€) *</label>
            <input v-model.number="service.prix" type="number" min="0" step="0.01"
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Durée (min) *</label>
            <input v-model.number="service.duree" type="number" min="15" step="15"
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>
        </div>

        <div class="space-y-1">
          <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Description (optionnel)</label>
          <textarea v-model="service.description" rows="2"
            class="w-full p-3 rounded-xl border text-xs font-medium focus:outline-none resize-none transition-all"
            :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white placeholder-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 placeholder-zinc-400 shadow-sm'"></textarea>
        </div>

        <div class="flex items-center gap-3">
          <input v-model="service.disponible_en_ligne" type="checkbox"
            class="h-4 w-4 rounded border cursor-pointer transition-all"
            :class="isDark ? 'accent-white border-zinc-700 bg-zinc-900' : 'accent-zinc-900 border-zinc-300 bg-white'" />
          <label class="text-xs font-medium transition-colors" :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Disponible pour réservation en ligne
          </label>
        </div>
      </div>

      <!-- Bouton ajouter un autre service -->
      <button @click="addService"
        class="w-full py-3 rounded-xl border-2 border-dashed text-xs font-semibold uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2"
        :class="isDark ? 'border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:bg-zinc-900/20' : 'border-zinc-300 text-zinc-400 hover:border-zinc-400 hover:bg-zinc-50'">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Ajouter un autre service
      </button>

      <!-- Actions -->
      <div class="flex gap-3 pt-4">
        <button @click="router.push('/services')"
          class="flex-1 px-4 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm"
          :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
          Annuler
        </button>
        <button @click="handleSubmitAll" :disabled="isSubmitting"
          class="flex-1 px-5 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 disabled:opacity-50 cursor-pointer shadow-sm"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          {{ isSubmitting ? 'Création en cours...' : `Créer ${services.length} service${services.length > 1 ? 's' : ''}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/api'
import { useTheme } from '@/composables/useTheme'

const { isDark } = useTheme()
const router = useRouter()
const isSubmitting = ref(false)
const categories = ref([])

const services = ref([{
  nom: '',
  description: '',
  prix: 0,
  duree: 30,
  categorie: '',
  disponible_en_ligne: true
}])

const addService = () => {
  services.value.push({
    nom: '',
    description: '',
    prix: 0,
    duree: 30,
    categorie: '',
    disponible_en_ligne: true
  })
}

const removeService = (index) => {
  if (services.value.length > 1) {
    services.value.splice(index, 1)
  }
}

onMounted(async () => {
  try {
    const response = await api.services.getAllCategories()
    categories.value = response.data.categories || response.data || []
  } catch (error) {
    console.error('Erreur lors du chargement:', error)
    toast.error('Erreur lors du chargement des catégories', { position: 'top-right' })
  }
})

const handleSubmitAll = async () => {
  // Validation
  for (let i = 0; i < services.value.length; i++) {
    const service = services.value[i]
    if (!service.nom || !service.prix || !service.duree || !service.categorie) {
      toast.error(`Service #${i + 1}: Veuillez remplir tous les champs obligatoires`, { position: 'top-right' })
      return
    }
  }

  try {
    isSubmitting.value = true
    let successCount = 0
    let errorCount = 0

    for (const service of services.value) {
      try {
        const serviceData = {
          nom: service.nom,
          description: service.description,
          prix: service.prix,
          duree: service.duree,
          categorie: service.categorie,
          disponibilite: service.disponible_en_ligne ? 'en_ligne' : 'salon',
          statut: 'actif'
        }

        await api.services.createService(serviceData)
        successCount++
      } catch (error) {
        console.error('Erreur création service:', error)
        errorCount++
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} service${successCount > 1 ? 's créés' : ' créé'} avec succès !`, {
        position: 'top-right',
        autoClose: 3000
      })
    }

    if (errorCount > 0) {
      toast.warning(`${errorCount} service${errorCount > 1 ? 's ont échoué' : ' a échoué'}`, {
        position: 'top-right',
        autoClose: 3000
      })
    }

    setTimeout(() => router.push('/services'), 1000)
  } catch (error) {
    console.error(error)
    toast.error('Erreur lors de la création des services', { position: 'top-right' })
  } finally {
    isSubmitting.value = false
  }
}
</script>
